<template>
    <div class="auth-dialog" v-if="visible">
        <div class="auth-content">
            <div class="auth-header">
                <h2>{{ isLoginMode ? '用户登录' : '用户注册' }}</h2>
                <span class="close-btn" @click="handleClose">×</span>
            </div>
            <div class="auth-form">
                <!-- 登录表单 -->
                <div v-if="isLoginMode">
                    <div class="form-item">
                        <input type="text" v-model="loginForm.username" placeholder="请输入手机号" />
                    </div>
                    <div class="form-item">
                        <input type="password" v-model="loginForm.password" placeholder="请输入密码" />
                    </div>
                    <div class="form-item captcha-item">
                        <input type="text" v-model="loginForm.code" placeholder="请输入验证码" />
                        <img :src="captchaImg" @click="refreshCaptcha" alt="验证码" class="captcha-img" />
                    </div>
                    <div class="form-item">
                        <button class="auth-submit" @click="handleLogin">登录</button>
                    </div>
                    <div class="switch-mode">
                        还没有账号？<span @click="isLoginMode = false">立即注册</span>
                    </div>
                </div>

                <!-- 注册表单 -->
                <div v-else>
                    <div class="form-item">
                        <input type="text" v-model="registerForm.userName" placeholder="请输入用户名（手机号）" required />
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <input type="password" v-model="registerForm.password" placeholder="请输入密码" required />
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <input type="text" v-model="registerForm.phone" placeholder="请输入电话号码" required />
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <input type="text" v-model="registerForm.name" placeholder="请输入姓名" required />
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <select v-model="registerForm.sex" required>
                            <option value="">请选择性别</option>
                            <option value="1">男</option>
                            <option value="0">女</option>
                        </select>
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <input type="text" v-model="registerForm.idCard" placeholder="请输入身份证号码" required />
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <input type="email" v-model="registerForm.email" placeholder="请输入邮箱" required />
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <select v-model="registerForm.province" @change="fetchSchoolList(registerForm.province)"
                            required>
                            <option value="">请选择报名所在地</option>
                            <option v-for="province in provinceList" :key="province.id" :value="province.id">
                                {{ province.name }}
                            </option>
                        </select>
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <select v-model="registerForm.addressProvince" required @focus="fetchAddressProvinceList">
                            <option value="">请选择籍贯</option>
                            <option v-for="province in addressProvinceList" :key="province.id" :value="province.id">
                                {{ province.name }}
                            </option>
                        </select>
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <select v-model="registerForm.school" required>
                            <option value="">请选择学校</option>
                            <template v-for="school in schoolList">
                                <option :key="school.id" :value="school.id">{{ school.label }}</option>
                                <template v-if="school.children && school.children.length">
                                    <option v-for="child in school.children" :key="child.id" :value="child.id">
                                        {{ child.label }}
                                    </option>
                                </template>
                            </template>
                        </select>
                        <span class="required-mark">*</span>
                    </div>
                    <div class="form-item">
                        <button class="auth-submit" @click="handleRegister">注册</button>
                    </div>
                    <div class="switch-mode">
                        已有账号？<span @click="isLoginMode = true">立即登录</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { login, getCaptchaImage, register, getAreaList, getSysDeptList } from '@/api/login'

export default {
    name: 'AuthDialog',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        initialMode: {
            type: String, // 'login' or 'register'
            default: 'login'
        }
    },
    data() {
        return {
            isLoginMode: true,
            loginForm: {
                username: '',
                password: '',
                code: '',
                uuid: ''
            },
            registerForm: {
                userName: '',
                password: '',
                phone: '',
                name: '',
                sex: '',
                idCard: '',
                email: '',
                province: '',
                addressProvince: '',
                school: ''
            },
            captchaImg: '',
            provinceList: [],
            schoolList: [],
            selectedSchool: '',
            addressProvinceList: [],
        }
    },
    watch: {
        visible(val) {
            if (val) {
                this.isLoginMode = (this.initialMode === 'login')
                if (this.isLoginMode) {
                    this.refreshCaptcha()
                } else {
                    this.fetchProvinceList()
                }
            }
        },
        isLoginMode(val) {
            if (val) {
                this.refreshCaptcha()
            } else {
                this.fetchProvinceList()
            }
        }
    },
    methods: {
        async fetchProvinceList() {
            try {
                const response = await getAreaList({ parentId: 1 })
                if (response && response.code === 200) {
                    this.provinceList = response.data || []
                } else {
                    console.error('Failed to fetch province list:', response.msg)
                }
            } catch (error) {
                console.error('Error fetching province list:', error)
            }
        },
        async fetchAddressProvinceList() {
            try {
                const response = await getAreaList({ parentId: 1 })
                if (response && response.code === 200) {
                    this.addressProvinceList = response.data || []
                } else {
                    console.error('Failed to fetch address province list:', response.msg)
                }
            } catch (error) {
                console.error('Error fetching address province list:', error)
            }
        },
        async fetchSchoolList(provinceId) {
            try {
                const response = await getSysDeptList({ province: provinceId })
                if (response && response.code === 200) {
                    this.schoolList = response.data || []
                } else {
                    console.error('获取学校列表失败:', response.msg)
                }
            } catch (error) {
                console.error('获取学校列表失败:', error)
            }
        },
        async refreshCaptcha() {
            try {
                const res = await getCaptchaImage()
                if (res.code === 200) {
                    this.captchaImg = 'data:image/jpeg;base64,' + res.img
                    this.loginForm.uuid = res.uuid
                }
            } catch (error) {
                console.error('获取验证码失败:', error)
            }
        },
        async handleLogin() {
            try {
                if (!this.loginForm.username || !this.loginForm.password || !this.loginForm.code) {
                    this.$message.error({
                        message: '请填写所有必填字段',
                        duration: 3000
                    })
                    return
                }
                const res = await login(this.loginForm)
                if (res.code === 200) {
                    console.log('登录返回的token:', res.token)
                    // 使用 Vuex 保存 token
                    await this.$store.dispatch('setToken', res.token)
                    // 获取用户信息
                    await this.$store.dispatch('fetchUserInfo')
                    this.$message.success('登录成功', { duration: 3000 })
                    this.handleClose()
                    this.$emit('login-success')
                } else {
                    this.$message.error(res.msg || '登录失败', { duration: 3000 })
                    this.refreshCaptcha()
                }
            } catch (error) {
                console.error('登录失败:', error)
                this.$message.error('登录请求发送失败', { duration: 3000 })
                this.refreshCaptcha()
            }
        },
        async handleRegister() {
            try {
                // 表单验证
                const requiredFields = {
                    userName: '用户名',
                    password: '密码',
                    phone: '电话号码',
                    name: '姓名',
                    sex: '性别',
                    idCard: '身份证号码',
                    email: '邮箱',
                    province: '报名所在地',
                    addressProvince: '籍贯',
                    school: '学校'
                }

                for (const [key, label] of Object.entries(requiredFields)) {
                    if (!this.registerForm[key]) {
                        this.$message.error({
                            message: `请填写${label}`,
                            duration: 3000
                        })
                        return
                    }
                }

                const res = await register(this.registerForm)
                if (res.code === 200) {
                    this.$message.success('注册成功，请登录！', { duration: 3000 })
                    this.isLoginMode = true // 注册成功后切换到登录模式
                    this.refreshCaptcha()
                } else {
                    this.$message.error(res.msg || '注册失败', { duration: 3000 })
                }
            } catch (error) {
                console.error('注册失败:', error)
                this.$message.error('注册请求发送失败', { duration: 3000 })
            }
        },
        handleClose() {
            this.$emit('update:visible', false)
            // 重置表单
            this.loginForm = {
                username: '',
                password: '',
                code: '',
                uuid: ''
            }
            this.registerForm = {
                userName: '',
                password: '',
                phone: '',
                name: '',
                sex: '',
                idCard: '',
                email: '',
                province: '',
                addressProvince: '',
                school: ''
            }
        }
    }
}
</script>

<style scoped>
.auth-dialog {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.auth-content {
    background: #ffffff;
    border-radius: 8px;
    width: 450px;
    max-height: 80vh;
    padding: 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    overflow-y: auto;
}

.auth-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.auth-header h2 {
    margin: 0;
    font-size: 20px;
    color: #333;
    font-weight: bold;
}

.close-btn {
    font-size: 24px;
    color: #999;
    cursor: pointer;
}

.auth-form {
    margin-top: 16px;
}

.form-item {
    margin-bottom: 16px;
    position: relative;
}

.required-mark {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #fe6c6f;
    font-size: 16px;
}

.form-item input,
.form-item select {
    width: 100%;
    height: 40px;
    padding: 0 15px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 14px;
    background-color: #f8faff;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
}

.form-item input:invalid,
.form-item select:invalid {
    border-color: #fe6c6f;
}

.form-item input:focus:invalid,
.form-item select:focus:invalid {
    box-shadow: 0 0 0 2px rgba(254, 108, 111, 0.2);
}

.form-item input:focus,
.form-item select:focus {
    border-color: #a0a0a0;
    box-shadow: 0 0 0 2px rgba(160, 160, 160, 0.2);
}

.form-item input::placeholder {
    color: #a0a0a0;
}

.form-item select {
    padding-right: 30px;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%23a0a0a0%22%20d%3D%22M7%209L5%207h4l-2%202z%22%2F%3E%3C%2Fsvg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
}

.captcha-item {
    display: flex;
    gap: 12px;
}

.captcha-item input {
    flex: 1;
}

.captcha-img {
    height: 40px;
    border-radius: 4px;
    cursor: pointer;
    border: 1px solid #e0e0e0;
    background-color: #eafbe9;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.auth-submit {
    width: 100%;
    height: 40px;
    background: #fe6c6f;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
}

.auth-submit:hover {
    background: #ff4d4f;
}

.switch-mode {
    text-align: center;
    margin-top: 20px;
    font-size: 14px;
    color: #666;
}

.switch-mode span {
    color: #fe6c6f;
    cursor: pointer;
    font-weight: bold;
    margin-left: 5px;
}

.switch-mode span:hover {
    text-decoration: underline;
}
</style>